import React, { Fragment, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { IconPhoto, IconX, IconInfoCircle } from '@tabler/icons-react';
import { Gallery } from '@core/frontend/ui';
import {
    Col,
    Card,
    CardImg,
    CardImgOverlay,
    Spinner,
    Button,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Alert,
    CardBody,
} from 'reactstrap';
import classes from './MediaUpload.module.scss';
import axios from '../../http/httpClient.js';
import { FormInput } from '@core/frontend/ui';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILE_COUNT = 9;
const ACCEPTED_FILE_FORMATS = 'JPG, JPEG und PNG';

const initialErrorsState = {
    invalidType: [],
    tooLarge: [],
    tooMany: [],
    serverError: [],
    other: [],
};

const hasErrors = (errors) => {
    if (!errors) return false;
    return (
        (errors.invalidType && errors.invalidType.length > 0) ||
        (errors.tooLarge && errors.tooLarge.length > 0) ||
        (errors.tooMany && errors.tooMany.length > 0) ||
        (errors.serverError && errors.serverError.length > 0) ||
        (errors.other && errors.other.length > 0)
    );
};

function MediaUpload(props) {
    const [fileToDelete, setFileToDelete] = useState({});
    const [processedFiles, setProcessedFiles] = useState([]);
    const [unprocessedFiles, setUnprocessedFiles] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [isLoadingFiles, setIsLoadingFiles] = useState(false);
    const [serverUrls, setServerUrls] = useState([]);
    const [showNotice, setShowNotice] = useState(false);
    const [uploadErrors, setUploadErrors] = useState(initialErrorsState);
    const [altTexts, setAltTexts] = useState({});
    const { getRootProps, getInputProps } = useDropzone({
        accept: {
            'image/png': ['.png'],
            'image/jpg': ['.jpg'],
            'image/jpeg': ['.jpeg'],
        },
        maxSize: MAX_FILE_SIZE,
        disabled: isLoadingFiles,
        onDrop: (acceptedFiles, fileRejections) => {
            const currentErrors = {
                invalidType: [],
                tooLarge: [],
                tooMany: [],
                serverError: [],
                other: [],
            };

            fileRejections.forEach(({ file, errors: fileErrors }) => {
                const fileCodes = new Set(fileErrors.map((e) => e.code));

                if (fileCodes.has('file-invalid-type')) {
                    currentErrors.invalidType.push(file.name);
                }
                if (fileCodes.has('file-too-large')) {
                    currentErrors.tooLarge.push(file.name);
                }
                if (fileCodes.has('too-many-files')) {
                    currentErrors.tooMany.push(file.name);
                }
                fileErrors.forEach((e) => {
                    if (e.code !== 'file-invalid-type' && e.code !== 'file-too-large' && e.code !== 'too-many-files') {
                        if (!currentErrors.other.includes(file.name)) {
                            currentErrors.other.push(file.name);
                        }
                    }
                });
            });

            const remainingSlots = Math.max(0, MAX_FILE_COUNT - processedFiles.length);
            const validFilesToUpload = acceptedFiles.slice(0, remainingSlots);
            const excessFiles = acceptedFiles.slice(remainingSlots);

            excessFiles.forEach((file) => {
                currentErrors.tooMany.push(file.name);
            });

            if (validFilesToUpload.length === 0) {
                setUploadErrors(currentErrors);
                setShowNotice(hasErrors(currentErrors));
                return;
            }

            displayUnprocessedFiles(validFilesToUpload);
            uploadFiles(validFilesToUpload, currentErrors);
        },
    });

    const displayUnprocessedFiles = (files) => {
        files.map((file) =>
            Object.assign(file, {
                preview: URL.createObjectURL(file),
            })
        );
        setUnprocessedFiles([...files]);
    };

    const uploadFiles = (filesToUpload, currentErrors) => {
        setIsLoadingFiles(true);
        const arrayOfPostedFormData = filesToUpload.map((file) => {
            const fd = new FormData();
            const axiosConfig = {
                onUploadProgress: (progressEvent) => {
                    return (progressEvent.loaded * 100) / progressEvent.total;
                },
            };
            fd.append('projectGallery', file);

            // Initialize alt text for the file
            setAltTexts((prevAltTexts) => ({
                ...prevAltTexts,
                [file.name]: '',
            }));

            return axios.post('/media', fd, axiosConfig);
        });

        Promise.allSettled(arrayOfPostedFormData).then((results) => {
            const successfulFiles = [];
            const successfulResponses = [];
            const updatedErrors = {
                ...currentErrors,
                serverError: [...(currentErrors.serverError || [])],
            };

            results.forEach((result, index) => {
                if (result.status === 'fulfilled') {
                    successfulFiles.push(filesToUpload[index]);
                    successfulResponses.push(result.value.data.projectGallery[0]);
                } else {
                    updatedErrors.serverError.push(filesToUpload[index].name);
                }
            });

            if (successfulFiles.length > 0) {
                setProcessedFiles((prevState) => [...prevState, ...successfulFiles]);
                setServerUrls((prevState) => [...prevState, ...successfulResponses]);
            }

            setUnprocessedFiles([]);
            setUploadErrors(updatedErrors);
            setShowNotice(hasErrors(updatedErrors));
            setIsLoadingFiles(false);
        });
    };

    const removePromptHandler = (file) => {
        modalHandler();
        setFileToDelete(file);
    };

    const modalHandler = () => {
        // console.log("modalHandler Called!");
        setShowModal(!showModal);
    };

    const removeFileHandler = () => {
        // console.log("removeFileHandler Called!");
        if (fileToDelete) {
            URL.revokeObjectURL(fileToDelete.preview);
            const indexOfProcessedFile = processedFiles.indexOf(fileToDelete);
            const indexOfUnProcessedFile = unprocessedFiles.indexOf(fileToDelete);
            //update the form urls
            setServerUrls((prevState) => {
                console.log('Prev State of form urls: ', prevState);
                console.log(
                    'FILE URL found to be removed: ',
                    prevState.filter((url) => url === fileToDelete.url)
                );
                // `File.path` gibt es im Browser nicht (nur bei Electron-Dateien);
                // `originalname` der Upload-Antwort traegt den Dateinamen. Ohne den
                // Rueckfall auf `name` blieb die hochgeladene URL beim Entfernen stehen.
                return prevState.filter(
                    (response) => response.originalname !== (fileToDelete.path ?? fileToDelete.name)
                );
            });
            if (indexOfProcessedFile >= 0) {
                const newFiles = [...processedFiles];
                newFiles.splice(indexOfProcessedFile, 1);
                setProcessedFiles(newFiles);

                // Also remove alt text for this file
                setAltTexts((prevAltTexts) => {
                    const newAltTexts = { ...prevAltTexts };
                    delete newAltTexts[fileToDelete.name];
                    return newAltTexts;
                });
            }
            if (indexOfUnProcessedFile > 0) {
                const newFiles = [...unprocessedFiles];
                newFiles.splice(indexOfUnProcessedFile, 1);
                setUnprocessedFiles(newFiles);
            }
        }
        setIsLoadingFiles(false);
        modalHandler();
    };

    const dismissAlert = () => {
        setShowNotice(false);
    };

    const handleAltTextChange = (fileName, value) => {
        setAltTexts((prevAltTexts) => ({
            ...prevAltTexts,
            [fileName]: value,
        }));
    };

    useEffect(() => {
        // Make sure to revoke the data uris to avoid memory leaks
        return () => {
            [...processedFiles, ...unprocessedFiles].map((file) => URL.revokeObjectURL(file.preview));

            // console.log("File URLs have been revoked");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setUnprocessedFiles([]);

        // Create new array with URLs and alt text in the required format
        const formattedUrls = serverUrls.map((item) => {
            // Find the corresponding processed file to get its name for the alt text lookup.
            // Ein Browser-`File` traegt keinen `path` (den gibt es nur bei Electron-Dateien);
            // die Antwort des Uploads fuehrt dagegen `originalname`. Ohne den Namensvergleich
            // blieb der eingegebene Alternativtext immer leer.
            const matchingFile =
                processedFiles.find((file) => file.path === item.originalname) ??
                processedFiles.find((file) => file.name === item.originalname);
            const altText = matchingFile ? altTexts[matchingFile.name] || '' : '';

            return {
                url: item.transforms[0].location,
                alt: altText,
            };
        });

        props.onUploadHandler(formattedUrls);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processedFiles, serverUrls, altTexts]);

    const processedThumbs = processedFiles.map((file, index) => {
        return (
            <Col xs="12" s="6" md="6" lg="4" className="my-2" key={file.name}>
                <Card className={classes.MediaFile}>
                    <CardImg
                        className={classes.CardImg}
                        // className={classes.CardImg}
                        top
                        width="100%"
                        src={file.preview}
                        alt={altTexts[file.name] || `Bild ${index + 1} vollständig hochgeladen!`}
                    />
                    <CardImgOverlay style={{ height: 'min-content', width: 'min-content', justifySelf: 'end' }}>
                        <div className={classes.deleteArea}>
                            <Button
                                onClick={() => removePromptHandler(file)}
                                disabled={isLoadingFiles}
                                color="danger"
                                className={classes.buttonDelete}
                                title={`Bild ${index + 1} entfernen`}
                            >
                                <IconX className={classes.icon} size={32} />
                            </Button>
                        </div>
                    </CardImgOverlay>
                    <CardBody>
                        <FormInput
                            elementConfig={{
                                label: `Beschreibung für Bild ${index + 1}`,
                                type: 'textarea',
                                placeholder: 'Beispiel: Die Jugendfeuerwehr bei einer Löschübung am See.',
                            }}
                            wrapperCSSClass="mb-0"
                            maxLenght={150}
                            value={altTexts[file.name] || ''}
                            changed={(e) => handleAltTextChange(file.name, e.target.value)}
                            id={`alt-text-${index}`}
                        />
                    </CardBody>
                </Card>
            </Col>
        );
    });

    const unProcessedThumbs = unprocessedFiles.map((file, index) => (
        <Col xs="12" s="6" md="6" lg="4" className="my-2" key={index}>
            <Card className={isLoadingFiles ? classes.MediaFile + ' ' + classes.Loading : classes.MediaFile}>
                <div key={file.name}>
                    <CardImg
                        className={classes.CardImg}
                        // className={classes.CardImg}
                        top
                        width="100%"
                        src={file.preview}
                        alt={altTexts[file.name] || `Bild ${index + processedFiles.length} wird noch verarbeitet`}
                    />
                    <CardImgOverlay className={classes.cardImageOverlay}>
                        <div className={classes.spinnerParentShow}>
                            <Spinner style={{ width: '3rem', height: '3rem' }} />{' '}
                        </div>
                        <div className={classes.deleteArea}>
                            <Button
                                onClick={() => removePromptHandler(file)}
                                disabled={isLoadingFiles}
                                color="danger"
                                className={classes.buttonDelete}
                                title={`Bild ${index + 1} entfernen`}
                            >
                                <IconX className={classes.icon} size={32} />
                            </Button>
                        </div>
                    </CardImgOverlay>
                </div>
                <CardBody>
                    <FormInput
                        elementConfig={{
                            label: `Alternativtext für Bild ${index + 1}`,
                            type: 'text',
                            placeholder: 'Alternativtext eingeben',
                        }}
                        wrapperCSSClass="mb-0"
                        maxLenght={150}
                        value={altTexts[file.name] || ''}
                        changed={(e) => handleAltTextChange(file.name, e.target.value)}
                        id={`alt-text-${index}`}
                    />
                </CardBody>
            </Card>
        </Col>
    ));

    const strings = {
        removeModalHeader: 'Entfernen',
        removeModalMessage: 'Soll die Datei gelöscht werden?',
        removeModalAbortButton: 'Abbrechen',
        removeModalRemoveButton: 'Löschen',
        uploadFailedMessage:
            'Einige Bilder konnten nicht hochgeladen werden. Bitte Dateityp, -größe und -anzahl überprüfen (max. 9 Bilder). <strong>Die Bewerbung kann trotzdem abgesendet werden</strong>, aber die folgenden Bilder werden ausgelassen:',
        uploadInputLabel: 'Bilder hinzufügen (max. Größe 10MB je Bild)',
        uploadingStatusLabel: 'Bitte warten, bis die Bilder hochgeladen wurden',
    };

    const inputId = props.id ?? 'mediaUpload';
    const uploadInputLabel = `Bilder hinzufügen (max. ${MAX_FILE_COUNT} Bilder)`;

    return (
        <Fragment>
            <Modal centered isOpen={showModal}>
                <ModalHeader toggle={modalHandler}>{strings.removeModalHeader}</ModalHeader>
                <ModalBody>
                    {strings.removeModalMessage}
                    <div>{fileToDelete.path}</div>
                </ModalBody>
                <ModalFooter>
                    <Button outline color="secondary" onClick={modalHandler}>
                        {strings.removeModalAbortButton}
                    </Button>{' '}
                    <Button color="secondary" onClick={removeFileHandler}>
                        {strings.removeModalRemoveButton}
                    </Button>
                </ModalFooter>
            </Modal>
            <section>
                <aside>
                    <Gallery isSignup>
                        {processedThumbs}
                        {unProcessedThumbs}
                    </Gallery>
                </aside>
                <Alert isOpen={showNotice} toggle={dismissAlert} color="warning" className="d-flex align-items-start">
                    <IconInfoCircle className="me-2 flex-shrink-0 mt-1" size={20} aria-hidden="true" />
                    <div className="w-100">
                        <div className="fw-bold mb-1">Einige Dateien konnten nicht hochgeladen werden.</div>
                        <p className="small mb-2">
                            Bitte die Anforderungen überprüfen. Das Formular kann dennoch mit den erfolgreich
                            hochgeladenen Bildern abgesendet werden:
                        </p>

                        {uploadErrors.invalidType?.length > 0 && (
                            <div className="mb-2">
                                <div className="fw-semibold small">
                                    Nicht unterstütztes Dateiformat{' '}
                                    <span className="text-muted fw-normal">(Erlaubt: {ACCEPTED_FILE_FORMATS})</span>:
                                </div>
                                <ul className="mb-0 ps-3 small">
                                    {uploadErrors.invalidType.map((fileName, index) => (
                                        <li key={`invalid-type-${fileName}-${index}`}>{fileName}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {uploadErrors.tooLarge?.length > 0 && (
                            <div className="mb-2">
                                <div className="fw-semibold small">
                                    Datei zu groß <span className="text-muted fw-normal">(Maximal 10 MB je Bild)</span>:
                                </div>
                                <ul className="mb-0 ps-3 small">
                                    {uploadErrors.tooLarge.map((fileName, index) => (
                                        <li key={`too-large-${fileName}-${index}`}>{fileName}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {uploadErrors.tooMany?.length > 0 && (
                            <div className="mb-2">
                                <div className="fw-semibold small">
                                    Maximale Anzahl überschritten{' '}
                                    <span className="text-muted fw-normal">
                                        (Maximal {MAX_FILE_COUNT} Bilder insgesamt)
                                    </span>
                                    :
                                </div>
                                <ul className="mb-0 ps-3 small">
                                    {uploadErrors.tooMany.map((fileName, index) => (
                                        <li key={`too-many-${fileName}-${index}`}>{fileName}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {uploadErrors.serverError?.length > 0 && (
                            <div className="mb-2">
                                <div className="fw-semibold small">
                                    Fehler bei der Übertragung{' '}
                                    <span className="text-muted fw-normal">
                                        (Upload fehlgeschlagen, bitte erneut versuchen)
                                    </span>
                                    :
                                </div>
                                <ul className="mb-0 ps-3 small">
                                    {uploadErrors.serverError.map((fileName, index) => (
                                        <li key={`server-error-${fileName}-${index}`}>{fileName}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {uploadErrors.other?.length > 0 && (
                            <div className="mb-2">
                                <div className="fw-semibold small">Sonstige Fehler:</div>
                                <ul className="mb-0 ps-3 small">
                                    {uploadErrors.other.map((fileName, index) => (
                                        <li key={`other-${fileName}-${index}`}>{fileName}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </Alert>
                <div
                    {...getRootProps({
                        id: `${inputId}Dropzone`,
                        role: 'button',
                        tabIndex: isLoadingFiles ? -1 : 0,
                        'aria-label': uploadInputLabel,
                        className: `rounded py-3 px-2 d-flex flex-column align-items-center justify-content-center text-center w-100 mb-0 ${classes.Dropzone} ${
                            isLoadingFiles ? classes.dropzoneDisabled : classes.dropzoneEnabled
                        }`,
                    })}
                >
                    <svg className={classes.borderSvg} aria-hidden="true">
                        <rect
                            className={classes.borderRect}
                            x="1"
                            y="1"
                            width="calc(100% - 2px)"
                            height="calc(100% - 2px)"
                            rx="6"
                        />
                    </svg>
                    {!isLoadingFiles && (
                        <input
                            {...getInputProps()}
                            id={inputId}
                            name={uploadInputLabel}
                            aria-label={uploadInputLabel}
                        />
                    )}
                    {isLoadingFiles ? (
                        <span className="d-inline-flex align-items-center justify-content-center">
                            <Spinner size="sm" className="me-2" />
                            <span>{strings.uploadingStatusLabel}</span>
                        </span>
                    ) : (
                        <Fragment>
                            <span className="d-inline-flex align-items-center justify-content-center gap-2 mb-2">
                                <IconPhoto
                                    className={`flex-shrink-0 ${classes.iconPhoto}`}
                                    size={32}
                                    aria-hidden="true"
                                />
                                <span className="fw-bold">Bilder hinzufügen</span>
                            </span>
                            <span className={`d-flex flex-column align-items-center gap-1 ${classes.dropzoneInfo}`}>
                                <span>Unterstützte Bildformate: {ACCEPTED_FILE_FORMATS}</span>
                                <span>Maximale Bildgröße: 10MB je Bild</span>
                                <span>Insgesamt können bis zu {MAX_FILE_COUNT} Bilder hochgeladen werden</span>
                            </span>
                        </Fragment>
                    )}
                </div>
            </section>
        </Fragment>
    );
}

export default MediaUpload;
